"use client";

import * as Sentry from "@sentry/nextjs";
import { RefObject, useEffect, useRef, useState } from "react";

interface UseSentryFeedbackOptions {
  /** Passed through to `Sentry.getFeedback().attachTo` as the feedback
   *  modal's title. */
  formTitle: string;
}

/**
 * Wires an element to Sentry's user-feedback widget: checks
 * `Sentry.getFeedback()` availability (undefined when the integration isn't
 * registered/enabled — see `sentry.client.config.ts`'s prod-only gate) and,
 * when available, calls `feedback.attachTo(ref.current, { formTitle })` so
 * clicking the element opens the feedback form.
 *
 * The single attach mechanism behind every "Give feedback" trigger in
 * the app: `BugReportButton`, `components/common/FeedbackButton` (mounted
 * in `DashboardContentLayout` and `MessagesPageClientNew`), the
 * newsletter beta banner (`NewsletterBetaBanner`, via `FeedbackButton`'s
 * `"link"` variant), the import failure card's "Report a problem"
 * (`ReportImportProblemButton` in `StreamlinedImportOverlay`), and
 * `@punchup/messaging`'s `MessagesFeedbackButton`. Each consumer owns its own ref and only mounts its
 * trigger element once it's actually rendering it, so this hook's effect
 * always runs with `ref.current` already populated — there's no
 * conditionally-rendered-elsewhere window to guard against, which is why
 * this hook takes no `enabled` flag.
 *
 * Returns `isAvailable`, so a caller can render nothing (or hide just the
 * trigger) when the integration is absent, matching `BugReportButton`'s
 * render-null behavior.
 */
export function useSentryFeedback(
  ref: RefObject<HTMLElement>,
  { formTitle }: UseSentryFeedbackOptions
): boolean {
  const [isAvailable, setIsAvailable] = useState(
    () => typeof window !== "undefined" && !!Sentry.getFeedback()
  );
  const hasWarnedRef = useRef(false);

  useEffect(() => {
    const feedback = Sentry.getFeedback();
    if (!feedback) {
      if (!hasWarnedRef.current) {
        console.warn("Sentry feedback integration not available");
        hasWarnedRef.current = true;
      }
      setIsAvailable(false);
      return;
    }
    setIsAvailable(true);
    if (!ref.current) return;
    const unsubscribe = feedback.attachTo(ref.current, { formTitle });
    return () => {
      unsubscribe?.();
    };
  }, [formTitle, ref]);

  return isAvailable;
}
